/**
 * Shared color primitives. Intentionally tiny and dep-free — used by
 * category chip styling, bank color resolution, and any future palette
 * consumer that needs hex parsing / tinting / darkening.
 */

export function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n;
}

export function parseHex(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return [148, 163, 184]; // slate-400 fallback
  const v = parseInt(m[1]!, 16);
  return [(v >> 16) & 0xff, (v >> 8) & 0xff, v & 0xff];
}

export function hexWithAlpha(hex: string, alpha: number): string {
  const [r, g, b] = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${clamp(alpha, 0, 1)})`;
}

export function darken(hex: string, amount: number): string {
  const [r, g, b] = parseHex(hex);
  const f = clamp(1 - amount, 0, 1);
  const dr = Math.round(r * f);
  const dg = Math.round(g * f);
  const db = Math.round(b * f);
  return `#${[dr, dg, db].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
}

/** WCAG 2.1 relative luminance (0..1). Input is a 6-digit hex string. */
export function relativeLuminance(hex: string): number {
  const [r, g, b] = parseHex(hex);
  const channel = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio between two hex colors. Always ≥ 1, ≤ 21. */
export function contrastRatio(hexA: string, hexB: string): number {
  const a = relativeLuminance(hexA);
  const b = relativeLuminance(hexB);
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);
  return (lighter + 0.05) / (darker + 0.05);
}
